import { getMessages } from "./i18n";
import { pickLocalized } from "./localized";
import { seoLinks } from "./seo";
import { resolveSiteContext } from "./site";
import { accessories } from "@/data/accessories";

const DEFAULT_OG_IMAGE = "https://inext.ltd/images/blog/tv6-ultra-hero.jpg";

export function pageSeo(
  locationHref: string,
  pathname: string,
  title: string,
  description: string,
  ogImage?: string,
  robots?: string,
) {
  const site = resolveSiteContext(locationHref);
  const resolvedImage =
    ogImage && /^https?:\/\//i.test(ogImage)
      ? ogImage
      : new URL(ogImage ?? DEFAULT_OG_IMAGE, site.origin).href;
  const links = seoLinks(pathname, site);
  const canonical = links.find(({ rel }) => rel === "canonical")?.href;

  return {
    meta: [
      { title },
      { name: "description", content: description },
      ...(robots ? [{ name: "robots", content: robots }] : []),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      ...(canonical ? [{ property: "og:url", content: canonical }] : []),
      { property: "og:site_name", content: "inext" },
      { property: "og:image", content: resolvedImage },
      { property: "og:image:alt", content: title },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: resolvedImage },
    ],
    links,
  };
}

export function homeSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/", messages.homeTitle, messages.homeDescription);
}

export function contactsSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/contacts", messages.contactsTitle, messages.contactsDescription);
}

export function aboutSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/about", messages.aboutTitle, messages.aboutDescription);
}

export function brandSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/brand", messages.brandSeoTitle, messages.brandSeoDescription);
}

export function distributorsSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/distributors",
    messages.distributorsTitle,
    messages.distributorsDescription,
  );
}

export function privacySeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/privacy", messages.privacyTitle, messages.privacyDescription);
}

export function newsletterSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/newsletter",
    messages.newsletterSeoTitle,
    messages.newsletterSeoDescription,
  );
}

export function faqSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(locationHref, "/faq", messages.faqTitle, messages.faqDescription);
}

export function republishingSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/republishing",
    messages.republishingTitle,
    messages.republishingDescription,
  );
}

export function searchSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/search",
    messages.searchTitle,
    messages.searchDescription,
    undefined,
    "noindex, follow",
  );
}

export function solutionsIndexSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions",
    messages.solutionsIndexSeoTitle,
    messages.solutionsIndexSeoDescription,
  );
}

export function productsIndexSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products",
    messages.productsIndexSeoTitle,
    messages.productsIndexSeoDescription,
  );
}

export function guidesIndexSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides",
    messages.guidesIndexSeoTitle,
    messages.guidesIndexSeoDescription,
  );
}

export function stbBuyersChecklistSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/iptv-stb-buyers-checklist",
    messages.sbcSeoTitle,
    messages.sbcSeoDescription,
    ogImage,
  );
}

export function stbTcoCalculatorSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/tools/stb-tco-calculator",
    messages.tcoSeoTitle,
    messages.tcoSeoDescription,
  );
}

export function stbRamStorageGuideSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/stb-ram-and-storage",
    messages.rsgSeoTitle,
    messages.rsgSeoDescription,
    ogImage,
  );
}

export function androidVsLinuxStbSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/android-vs-linux-stb",
    messages.avlSeoTitle,
    messages.avlSeoDescription,
  );
}

export function aospVsCertifiedAtvSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/aosp-vs-certified-android-tv",
    messages.acSeoTitle,
    messages.acSeoDescription,
  );
}

export function ownStbVsSmartTvSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/own-stb-vs-smart-tv",
    messages.osSeoTitle,
    messages.osSeoDescription,
  );
}

export function subsidizeLeaseSellSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/subsidize-lease-or-sell",
    messages.bmSeoTitle,
    messages.bmSeoDescription,
  );
}

export function multicastVsOttSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/multicast-iptv-vs-ott",
    messages.moSeoTitle,
    messages.moSeoDescription,
  );
}

export function buildVsBuySeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/guides/build-vs-buy-stb",
    messages.bbSeoTitle,
    messages.bbSeoDescription,
  );
}

export function alcatrazSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/alcatraz-dms",
    messages.alcatrazSeoTitle,
    messages.alcatrazSeoDescription,
    ogImage,
  );
}

export function alcatrazStartSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/alcatraz-dms/get-started",
    messages.alcatrazStartSeoTitle,
    messages.alcatrazStartSeoDescription,
  );
}

export function alcatrazTrialSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/alcatraz-dms/trial",
    messages.alcatrazTrialSeoTitle,
    messages.alcatrazTrialSeoDescription,
    undefined,
    "noindex, nofollow",
  );
}

export function customizationSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/customization",
    messages.customPageSeoTitle,
    messages.customPageSeoDescription,
  );
}

export function otaUpdatesSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/ota-updates",
    messages.otaSeoTitle,
    messages.otaSeoDescription,
  );
}

export function iptvAppSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/iptv-app",
    messages.iptvAppSeoTitle,
    messages.iptvAppSeoDescription,
  );
}

export function logisticsSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/logistics",
    messages.logisticsSeoTitle,
    messages.logisticsSeoDescription,
  );
}

export function tv6UltraSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/inext-tv6-ultra",
    messages.tv6UltraSeoTitle,
    messages.tv6UltraSeoDescription,
    ogImage,
  );
}

export function tv6Seo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/inext-tv6",
    messages.tv6SeoTitle,
    messages.tv6SeoDescription,
    ogImage,
  );
}

export function tv5UltraSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/inext-tv5-ultra",
    messages.tv5UltraSeoTitle,
    messages.tv5UltraSeoDescription,
    ogImage,
  );
}

export function tv5Seo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/inext-tv5",
    messages.tv5SeoTitle,
    messages.tv5SeoDescription,
    ogImage,
  );
}

export function accessoriesSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/accessories",
    messages.accessoriesSeoTitle,
    messages.accessoriesSeoDescription,
  );
}

export function archiveSeo(locationHref: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/products/archive",
    messages.archiveSeoTitle,
    messages.archiveSeoDescription,
  );
}

export function accessoryDetailSeo(locationHref: string, id: string) {
  const site = resolveSiteContext(locationHref);
  const accessory = accessories.find((a) => a.id === id);

  if (!accessory) {
    return accessoriesSeo(locationHref);
  }

  const locale = site.locale;
  const title = pickLocalized(accessory, locale, "title");
  const description = pickLocalized(accessory, locale, "shortDesc");

  return pageSeo(
    locationHref,
    `/products/accessories/${id}`,
    `${title} | inext`,
    description,
    accessory.image,
  );
}

export function remoteAppSeo(locationHref: string, ogImage?: string) {
  const site = resolveSiteContext(locationHref);
  const messages = getMessages(site.locale);
  return pageSeo(
    locationHref,
    "/solutions/inext-remote",
    messages.remoteAppSeoTitle,
    messages.remoteAppSeoDescription,
    ogImage,
  );
}

// blogIndexSeo / blogPostSeo live in ./blog-seo, not here: they need ./blog's
// full post-loading module (yaml + eager glob of every locale's Markdown), and
// this file is imported by every route's `head()` for its generic pageSeo()
// helper. Keeping them out of here keeps that heavy module out of every
// non-blog route's client bundle.
